/**
 * Moteur d'inférence pédagogique pour les fiches d'évaluation interactives.
 *
 * RACCORDÉ AU LIVRET D'APPRENTISSAGE REMC (catégorie A) — source de vérité :
 *   /app/frontend/src/data/livretApprentissageA.js
 *
 * À partir des cases cochées par l'enseignant en éval statique + dynamique :
 *  - propose 1 à 3 axes prioritaires de travail
 *  - chaque axe = 1 sous-compétence OFFICIELLE du livret (ex : C1-e, C2-d, …)
 *  - retourne l'objectif officiel, le code, le libellé, et un slug fiche cours si dispo
 *
 * Règle de priorisation :
 *  1. Critère Oui/Non = "non" sur évaluation dynamique → poids 100
 *  2. Compétence observée = "non" sur évaluation circulation → poids 80
 *  3. Appréhension "important" → poids 70
 *  4. Compétence pas dans "déjà abordées" → poids 30 (signal faible)
 *  → Tri descendant, top 3 retournés.
 */
import { COMPETENCES_LIVRET } from '../livretApprentissageA';
import { getObjectifConcis, getObjectifSavoirCourt } from './objectifsConcis';

/* ─── Helpers livret ─────────────────────────────────────────────── */

/**
 * Index global { 'C1-e': { ...sousCompetence, categorie: 'C1' } }
 * pour accès O(1) à toute sous-compétence officielle.
 */
const LIVRET_INDEX = (() => {
  const idx = {};
  COMPETENCES_LIVRET.forEach((cat) => {
    cat.sousCompetences.forEach((sc) => {
      idx[sc.code] = {
        ...sc,
        categorie: cat.code,
        categorieTitre: cat.titre,
      };
    });
  });
  return idx;
})();

/** Récupère une sous-compétence officielle du livret par son code. */
export function getLivretItem(code) {
  return LIVRET_INDEX[code] || null;
}

/**
 * Génère la liste plate des sous-compétences du livret pour usage UI
 * (checkboxes, sélecteurs). Permet de filtrer par catégorie, d'inclure
 * des codes hors catégorie (ex C1-b dans Circulation) ou d'exclure des
 * codes (ex C1-b hors Plateau).
 *
 * Retourne : Array<{ code, intitule, categorie, categorieTitre, objectif, criteres }>
 *
 * Préserve l'ordre officiel du livret.
 */
export function getSousCompetencesLivret({
  categories = [],
  includeCodes = [],
  excludeCodes = [],
} = {}) {
  const result = [];
  const seen = new Set();
  COMPETENCES_LIVRET.forEach((cat) => {
    if (categories.length && !categories.includes(cat.code)) return;
    cat.sousCompetences.forEach((sc) => {
      if (excludeCodes.includes(sc.code)) return;
      if (seen.has(sc.code)) return;
      seen.add(sc.code);
      result.push({
        ...sc,
        categorie: cat.code,
        categorieTitre: cat.titre,
      });
    });
  });
  // Ajoute les codes inclus explicitement (peut être cross-catégorie)
  includeCodes.forEach((code) => {
    if (seen.has(code) || excludeCodes.includes(code)) return;
    const item = LIVRET_INDEX[code];
    if (item) {
      seen.add(code);
      result.push(item);
    }
  });
  return result;
}

/**
 * Transforme un objectif "À l'issue de la séance, l'élève …" en une
 * formulation pédagogique "Savoir …" plus directe pour la fiche d'éval.
 */
function toSavoirObjective(item) {
  if (!item) return '';
  const raw = item.objectif || '';
  // Capture après "élève " pour reformuler en "Savoir"
  const m = raw.match(/[ée]l[èe]ve\s+(.+)/i);
  if (m) {
    let verb = m[1].trim();
    // Conjugue la 3e personne en infinitif (heuristique légère)
    verb = verb
      .replace(/^identifie\b/, 'identifier')
      .replace(/^vérifie\b/, 'vérifier')
      .replace(/^adopte\b/, 'adopter')
      .replace(/^cite\b/, 'citer')
      .replace(/^applique\b/, 'appliquer')
      .replace(/^démarre\b/, 'démarrer')
      .replace(/^maintient\b/, 'maintenir')
      .replace(/^module\b/, 'moduler')
      .replace(/^réalise\b/, 'réaliser')
      .replace(/^négocie\b/, 'négocier')
      .replace(/^utilise\b/, 'utiliser')
      .replace(/^franchit\b/, 'franchir')
      .replace(/^s'insère\b/, "s'insérer")
      .replace(/^suit\b/, 'suivre')
      .replace(/^évalue\b/, 'évaluer')
      .replace(/^prépare\b/, 'préparer')
      .replace(/^connaît\b/, 'connaître')
      .replace(/^positionne\b/, 'positionner')
      .replace(/^dirige\b/, 'diriger')
      .replace(/^croise\b/, 'croiser')
      .replace(/^évite\b/, 'éviter')
      .replace(/^circule\b/, 'circuler')
      .replace(/^pratique\b/, 'pratiquer');
    return `Savoir ${verb}`.replace(/\.\s*$/, '');
  }
  return `Savoir maîtriser : ${item.intitule}`;
}

/* ─── Fiches cours câblées par code REMC (Plateau + Séance Circulation) ── */
/* Chaque entrée pointe vers un slug + son hub de base.
   hub: '/enseignant/terrain'           → fiches Plateau (C1)
   hub: '/enseignant/seance-circulation' → fiches Circulation REMC (C2/C3/C4)
   Note : `fichesPlateau.js` utilisait des codes REMC obsolètes. Ici on
   recâble proprement sur la nomenclature officielle du livret.            */
export const FICHE_BY_CODE = {
  'C1-e': { slug: 'demarrer-sarreter',     hub: '/enseignant/terrain' },
  'C1-g': { slug: 'freinage-urgence',       hub: '/enseignant/terrain' },
  'C1-h': { slug: 'tourner-ralenti-moteur', hub: '/enseignant/terrain' },
  'C1-i': { slug: 'demi-tour-serre',        hub: '/enseignant/terrain' },
  'C1-k': { slug: 'slalom-allure',          hub: '/enseignant/terrain' },
  'C1-l': { slug: 'freinage-urgence',       hub: '/enseignant/terrain' },
  // ─── C2 — SÉANCES CIRCULATION (8 fiches REMC complètes) ───
  'C2-a': { slug: 'connaitre-respecter-regles',     hub: '/enseignant/seance-circulation' },
  'C2-b': { slug: 'rechercher-signalisation-indices', hub: '/enseignant/seance-circulation' },
  'C2-c': { slug: 'positionner-chaussee',           hub: '/enseignant/seance-circulation' },
  'C2-d': { slug: 'adapter-allure',                  hub: '/enseignant/seance-circulation' },
  'C2-e': { slug: 'arret-stationnement-secu',       hub: '/enseignant/seance-circulation' },
  'C2-f': { slug: 'tourner-droite-gauche',          hub: '/enseignant/seance-circulation' },
  'C2-g': { slug: 'franchir-intersections',         hub: '/enseignant/seance-circulation' },
  'C2-h': { slug: 'franchir-ronds-points',          hub: '/enseignant/seance-circulation' },
  // ─── C3 — CONDITIONS DIFFICILES (11 fiches) ───
  'C3-a': { slug: 'distances-securite',         hub: '/enseignant/seance-circulation' },
  'C3-b': { slug: 'croiser-suivre-depasser',    hub: '/enseignant/seance-circulation' },
  'C3-c': { slug: 'negocier-virages-route',     hub: '/enseignant/seance-circulation' },
  'C3-d': { slug: 'communiquer-autres-usagers', hub: '/enseignant/seance-circulation' },
  'C3-e': { slug: 'connaitre-autres-usagers',   hub: '/enseignant/seance-circulation' },
  'C3-f': { slug: 'cohabiter-poids-lourds',     hub: '/enseignant/seance-circulation' },
  'C3-g': { slug: 'inserer-sortir-voie-rapide', hub: '/enseignant/seance-circulation' },
  'C3-h': { slug: 'circuler-file-dense',        hub: '/enseignant/seance-circulation' },
  'C3-i': { slug: 'circulation-inter-files',    hub: '/enseignant/seance-circulation' },
  'C3-j': { slug: 'adherence-visibilite-reduites', hub: '/enseignant/seance-circulation' },
  'C3-k': { slug: 'tunnels-ponts',              hub: '/enseignant/seance-circulation' },
  // ─── C4 — AUTONOMIE (7 fiches) ───
  'C4-a': { slug: 'suivre-itineraire-autonome', hub: '/enseignant/seance-circulation' },
  'C4-b': { slug: 'longue-distance',            hub: '/enseignant/seance-circulation' },
  'C4-c': { slug: 'facteurs-risque',            hub: '/enseignant/seance-circulation' },
  'C4-d': { slug: 'conduite-tenir-accident',    hub: '/enseignant/seance-circulation' },
  'C4-e': { slug: 'aides-conduite-abs',         hub: '/enseignant/seance-circulation' },
  'C4-f': { slug: 'entretien-moto',             hub: '/enseignant/seance-circulation' },
  'C4-g': { slug: 'eco-conduite',               hub: '/enseignant/seance-circulation' },
};

/* Compat : pour les composants qui consomment `slug` à plat. */
const FICHE_SLUG_BY_CODE = Object.fromEntries(
  Object.entries(FICHE_BY_CODE).map(([code, info]) => [code, info.slug]),
);

/* ─── Mapping REMC → contenus pédagogiques additionnels ──────────── */
/* Articles magazine + enquêtes ETM correspondant à chaque compétence.
   Sert quand il n'existe pas de fiche plateau dédiée OU en complément.
   Type : 'magazine' pour /magazine/:slug, 'enquete' pour /etm/:slug.    */
const LINKS_BY_CODE = {
  // ─── C1 — HORS CIRCULATION ───
  'C1-a': [{ slug: 'equipement-complet-motard-2026', label: 'EPI complet du motard', type: 'magazine' },
           { slug: 'bien-choisir-casque-moto',       label: 'Bien choisir son casque', type: 'magazine' },
           { slug: 'gants-moto-homologues',          label: 'Gants homologués', type: 'magazine' },
           { slug: 'equipement-moto-2026',           label: 'Enquête équipement motard', type: 'enquete' },
           { slug: 'equipement-motard',              label: 'Équipement motard — étude', type: 'enquete' }],
  'C1-b': [{ slug: 'physique-deux-roues', label: 'Physique du 2-roues', type: 'enquete' }],
  'C1-c': [{ slug: 'physique-deux-roues', label: 'Physique du 2-roues', type: 'enquete' }],
  'C1-d': [{ slug: 'equipement-complet-motard-2026', label: 'EPI & position', type: 'magazine' }],
  'C1-e': [],
  'C1-f': [{ slug: 'transmission-moto', label: 'Transmission moto expliquée', type: 'enquete' }],
  'C1-g': [{ slug: 'freinage-deux-roues', label: 'Freinage 2-roues — physique', type: 'enquete' }],
  'C1-h': [{ slug: 'physique-virages-moto',          label: 'Physique des virages', type: 'enquete' },
           { slug: 'trajectoire-securite-virage',    label: 'Trajectoire de sécurité', type: 'magazine' }],
  'C1-i': [{ slug: 'physique-deux-roues', label: 'Physique du 2-roues', type: 'enquete' }],
  'C1-j': [{ slug: 'transmission-moto', label: 'Transmission & embrayage', type: 'enquete' }],
  'C1-k': [{ slug: 'trajectoire-securite-virage',    label: 'Trajectoire de sécurité', type: 'magazine' },
           { slug: 'trajectoire-securite-virage-eie', label: 'Trajectoire EIE — étude de cas', type: 'enquete' },
           { slug: 'physique-virages-moto',          label: 'Physique des virages', type: 'enquete' }],
  'C1-l': [{ slug: 'freinage-deux-roues', label: 'Freinage 2-roues — physique', type: 'enquete' }],
  // ─── C2 — CIRCULATION normale ───
  'C2-a': [{ slug: 'passer-le-permis-a1-a2', label: 'Permis A1 / A2 — règles', type: 'magazine' },
           { slug: 'assr2-preparer-3eme',    label: 'ASSR 2 — règles essentielles', type: 'magazine' }],
  'C2-b': [{ slug: 'angle-mort-2-roues',       label: 'Angle mort 2-roues', type: 'enquete' },
           { slug: 'signalisation-am',         label: 'Signalisation — AM', type: 'enquete' }],
  'C2-c': [{ slug: 'trajectoire-securite-virage', label: 'Trajectoire de sécurité', type: 'magazine' }],
  'C2-d': [{ slug: 'distances-securite-moto', label: 'Distances de sécurité', type: 'magazine' },
           { slug: 'vitesse-jeune-permis',    label: 'Vitesse & jeunes permis', type: 'enquete' }],
  'C2-e': [{ slug: 'zfe-critair-motards', label: 'Stationnement & ZFE', type: 'magazine' }],
  'C2-f': [{ slug: 'trajectoire-securite-virage', label: 'Trajectoire de sécurité', type: 'magazine' }],
  'C2-g': [{ slug: 'signalisation-am',        label: 'Signalisation & priorités', type: 'enquete' },
           { slug: 'assr1-preparer-5eme',     label: 'ASSR 1 — bases priorités', type: 'magazine' }],
  'C2-h': [{ slug: 'signalisation-am',        label: 'Signalisation & ronds-points', type: 'enquete' }],
  // ─── C3 — CIRCULATION difficile ───
  'C3-a': [{ slug: 'distances-securite-moto', label: 'Distances de sécurité', type: 'magazine' },
           { slug: 'freinage-deux-roues',     label: 'Freinage 2-roues', type: 'enquete' }],
  'C3-b': [{ slug: 'angle-mort-2-roues', label: 'Angle mort 2-roues', type: 'enquete' }],
  'C3-c': [{ slug: 'trajectoire-securite-virage',    label: 'Trajectoire de sécurité', type: 'magazine' },
           { slug: 'physique-virages-moto',          label: 'Physique des virages', type: 'enquete' },
           { slug: 'trajectoire-securite-virage-eie', label: 'Trajectoire EIE — étude', type: 'enquete' }],
  'C3-d': [{ slug: 'eclairage-signalisation-moto', label: 'Éclairage & signalisation', type: 'enquete' }],
  'C3-e': [{ slug: 'livreurs-deux-roues',     label: 'Livreurs 2-roues', type: 'enquete' },
           { slug: 'risques-cyclo-am',        label: 'Risques cyclo / AM', type: 'enquete' }],
  'C3-f': [{ slug: 'angle-mort-2-roues', label: 'Angle mort PL', type: 'enquete' }],
  'C3-g': [{ slug: 'autoroute-moto-regles-2026', label: 'Autoroute moto — règles', type: 'magazine' }],
  'C3-h': [{ slug: 'circulation-am-ville', label: 'Circulation en ville (AM)', type: 'enquete' }],
  'C3-i': [{ slug: 'autoroute-moto-regles-2026', label: 'Voies rapides — règles', type: 'magazine' }],
  'C3-j': [{ slug: 'nuit-moto',                    label: 'Conduite de nuit', type: 'enquete' },
           { slug: 'eclairage-signalisation-moto', label: 'Éclairage & signalisation', type: 'enquete' },
           { slug: 'pneus-moto',                   label: 'Pneus moto — adhérence', type: 'enquete' }],
  'C3-k': [{ slug: 'eclairage-signalisation-moto', label: 'Éclairage tunnels & ponts', type: 'enquete' }],
  // ─── C4 — AUTONOMIE ───
  'C4-a': [{ slug: 'autoroute-moto-regles-2026', label: 'Préparer un trajet autoroute', type: 'magazine' }],
  'C4-b': [{ slug: 'fatigue-moto', label: 'Fatigue & longue distance', type: 'enquete' }],
  'C4-c': [{ slug: 'alcool-stupefiants-moto', label: 'Alcool & stupéfiants', type: 'magazine' },
           { slug: 'alcool-moto',             label: 'Alcool moto — étude', type: 'enquete' },
           { slug: 'telephone-oreillettes-moto', label: 'Téléphone & oreillettes', type: 'magazine' },
           { slug: 'risque-reel-vs-percu',    label: 'Risque réel vs perçu', type: 'enquete' },
           { slug: 'surestimation-motard',    label: 'Sur-estimation du motard', type: 'enquete' },
           { slug: 'vitesse-jeune-permis',    label: 'Vitesse & jeunes permis', type: 'enquete' }],
  'C4-d': [{ slug: 'construction-accident',  label: "Construction d'un accident", type: 'enquete' },
           { slug: 'fatalite-accident-moto', label: 'Fatalité accident moto', type: 'enquete' }],
  'C4-e': [{ slug: 'freinage-deux-roues', label: 'ABS & freinage 2-roues', type: 'enquete' }],
  'C4-f': [{ slug: 'pression-pneus-moto', label: 'Pression des pneus', type: 'enquete' },
           { slug: 'pneus-moto',          label: 'Pneus moto — entretien', type: 'enquete' }],
  'C4-g': [{ slug: 'transmission-moto', label: 'Transmission & éco-conduite', type: 'enquete' }],
};

/* ─── Mapping CHECKBOX → CODE REMC officiel ──────────────────────── */

/**
 * Critères Plateau (form.criteresRealises[key] = 'non')
 * Référence : labels affichés dans EvalPlateauForm.jsx ligne 241-249
 */
const PLATEAU_CRITERES_TO_REMC = {
  regardLoin:       'C1-h', // Regard porté loin → C1-h Incidences du regard
  positionAdaptee:  'C1-d', // Position adaptée → C1-d Position de conduite
  gestionAllure:    'C1-j', // Gestion allure → C1-j Embrayage / dosage
  coordination:     'C1-j', // Coordination commandes → C1-j Embrayage
  trajectoire:      'C1-k', // Trajectoire maîtrisée → C1-k Inclinaison
  stabilite:        'C1-i', // Stabilité moto → C1-i Équilibre
  respectConsignes: 'C1-b', // Respect consignes → C1-b Connaître la moto
};

/**
 * Ces deux tables historiques (label → code) deviennent OBSOLÈTES depuis
 * que les checkboxes Plateau & Circulation stockent directement les codes
 * REMC officiels. On les conserve uniquement comme fallback pour les
 * fiches d'évaluation qui auraient été sauvegardées AVANT cette migration
 * (compétences sous forme de label texte libre dans localStorage).
 *
 * Pour les nouvelles fiches, la valeur stockée EST déjà le code REMC,
 * donc on tente d'abord une recherche directe via getLivretItem(value).
 */
const PLATEAU_COMP_TO_REMC = {
  'Allure lente':        'C1-i',
  'Slalom':              'C1-i',
  'Demi-tour':           'C1-i',
  'Freinage':            'C1-g',
  'Évitement':           'C1-k',
  'Regard / trajectoire':'C1-h',
  'Gestion embrayage':   'C1-j',
  'Position sur la moto':'C1-d',
  'Contre-braquage':     'C1-k',
};

/**
 * Lignes d'appréhension Plateau (form.apprehension[key] = 'important')
 * Référence : APPREHENSIONS ligne 231-237 dans EvalPlateauForm.jsx
 */
const PLATEAU_APPREHENSION_TO_REMC = {
  stress:     'C4-c', // Facteurs de risque humains
  equilibre:  'C1-i', // Équilibre
  allure:     'C1-j', // Embrayage / dosage allure lente
  regard:     'C1-h', // Regard
  confiance:  'C4-c', // Conscience de soi
};

/**
 * Critères Circulation (form.criteresRealises[key] = 'non')
 * Référence : CRITERES_CIRC dans EvalCirculationForm.jsx ligne 91-101
 */
const CIRCULATION_CRITERES_TO_REMC = {
  allure:        'C2-d', // Adapter l'allure aux situations
  placement:     'C2-c', // Positionner sur la chaussée
  trajectoire:   'C3-c', // Négocier les virages
  regard:        'C2-b', // Recherche signalisation / indices
  anticipation:  'C2-b', // Indices utiles + adaptation
  respect:       'C2-a', // Règles de circulation
  communication: 'C3-d', // Communiquer avec autres usagers
  autonomie:     'C4-a', // Suivre itinéraire autonome
  courtoisie:    'C3-e', // Autres usagers / respect
};

/**
 * Compétences observées en circulation (form.competencesObservees[label] = 'non')
 * Référence : COMP_OBSERVEES ligne 66-73 dans EvalCirculationForm.jsx
 */
const CIRCULATION_COMP_OBSERVEES_TO_REMC = {
  'Connaissance du Code':       'C2-a',
  'Allure adaptée':             'C2-d',
  'Observation (regard)':       'C2-b',
  'Placement chaussée':         'C2-c',
  'Espaces de sécurité':        'C3-a',
  'Anticipation':               'C2-b',
  "Prise d'information":        'C2-b',
  'Communication usagers':      'C3-d',
  'Respect priorités/règles':   'C2-g',
  'Franchissement intersections':'C2-g',
  'Changement direction':       'C2-f',
  'Insertion / sortie':         'C3-g',
  'Gestion dépassements':       'C3-b',
  'Courtois et respectueux':    'C3-e',
  'Autonomie et itinéraire':    'C4-a',
  'Gestion imprévus':           'C4-d',
};

/**
 * Lignes d'appréhension Circulation (form.apprehension[key] = 'important')
 * Référence : APPREHENSION_LIGNES ligne 77-83 dans EvalCirculationForm.jsx
 */
const CIRCULATION_APPREHENSION_TO_REMC = {
  stress:    'C4-c',
  equilibre: 'C1-i',
  allure:    'C2-d',
  autres:    'C3-e', // Autres usagers
  confiance: 'C4-c',
};

/* ─── Builder de suggestion ──────────────────────────────────────── */

function buildSuggestion(remcCode, weight, source) {
  const item = getLivretItem(remcCode);
  if (!item) return null;
  const ficheInfo = FICHE_BY_CODE[remcCode] || null;
  const concis = getObjectifConcis(remcCode);
  const objectifSavoirCourt = getObjectifSavoirCourt(remcCode);
  return {
    key: remcCode,
    competenceCode: remcCode,
    categorie: item.categorie,
    competenceIntitule: item.intitule,
    competenceLabelLong: item.intitule,
    // ── Version CONCISE (prioritaire pour la fiche d'éval) ──
    // Verbe d'action court, ≤ 12 mots. Si pas de version concise dispo,
    // on retombe sur l'ancienne reformulation longue.
    objectifSavoir: objectifSavoirCourt || toSavoirObjective(item),
    objectifCourt: concis?.objectifCourt || null,
    criteresPrecis: concis?.criteresPrecis || null,
    // ── Version OFFICIELLE LIVRET (consultative, dépliée à la demande) ──
    objectifLivret: item.objectif,
    criteresLivret: item.criteres || [],
    // ── Ressources ──
    slug: ficheInfo ? ficheInfo.slug : null,
    ficheHub: ficheInfo ? ficheInfo.hub : null,
    ficheRoute: ficheInfo ? `${ficheInfo.hub}/${ficheInfo.slug}` : null,
    contenus: LINKS_BY_CODE[remcCode] || [],
    livretAnchor: `/livret#${remcCode}`,
    source,
    weight,
  };
}

function pushUnique(arr, suggestion) {
  if (!suggestion) return;
  // Si déjà présent, on garde le poids le plus fort
  const existing = arr.find((s) => s.competenceCode === suggestion.competenceCode);
  if (existing) {
    if (suggestion.weight > existing.weight) {
      existing.weight = suggestion.weight;
      existing.source = suggestion.source;
    }
    return;
  }
  arr.push(suggestion);
}

/* ─── INFÉRENCE PLATEAU ──────────────────────────────────────────── */

export function inferFromPlateau(form) {
  if (!form) return [];
  const suggestions = [];

  // 1. Critères dynamiques "non" → poids 100
  if (form.criteresRealises) {
    Object.entries(form.criteresRealises).forEach(([k, v]) => {
      if (v === 'non' && PLATEAU_CRITERES_TO_REMC[k]) {
        pushUnique(suggestions, buildSuggestion(
          PLATEAU_CRITERES_TO_REMC[k], 100, 'Critère non atteint'
        ));
      }
    });
  }

  // 2. Appréhensions "important" → poids 70
  if (form.apprehension) {
    Object.entries(form.apprehension).forEach(([k, v]) => {
      if (v === 'important' && PLATEAU_APPREHENSION_TO_REMC[k]) {
        pushUnique(suggestions, buildSuggestion(
          PLATEAU_APPREHENSION_TO_REMC[k], 70, 'Appréhension importante'
        ));
      }
    });
  }

  // 3. Niveau débutant → renforce sur fondamentaux
  if (form.niveauActuel === 'debutant') {
    pushUnique(suggestions, buildSuggestion('C1-e', 60, 'Niveau débutant — fondamentaux'));
    pushUnique(suggestions, buildSuggestion('C1-g', 55, 'Niveau débutant — freinage'));
  }

  // 4. Compétences "déjà abordées" (codes REMC directs ou labels legacy)
  const dejaVues = new Set(form.competencesAbordees || []);

  // Nouveau format : array de codes REMC directs ('C1-e', 'C2-d', ...)
  // → on signale les codes attendus mais ABSENTS comme axe à travailler
  const EXPECTED_C1_FOR_PLATEAU = ['C1-d','C1-e','C1-g','C1-h','C1-i','C1-j','C1-k','C1-l'];
  EXPECTED_C1_FOR_PLATEAU.forEach((code) => {
    if (!dejaVues.has(code) && !suggestions.find((s) => s.competenceCode === code)) {
      pushUnique(suggestions, buildSuggestion(code, 25, `« ${getLivretItem(code)?.intitule || code} » non encore abordé`));
    }
  });

  // Format legacy (label texte) : conserve l'ancien mapping
  Object.entries(PLATEAU_COMP_TO_REMC).forEach(([label, code]) => {
    if (dejaVues.has(label)) return;
    if (suggestions.find((s) => s.competenceCode === code)) return;
    // Si on a déjà détecté le code via format nouveau, on saute
    if (dejaVues.has(code)) return;
    pushUnique(suggestions, buildSuggestion(code, 25, `« ${label} » non encore abordé`));
  });

  return suggestions.sort((a, b) => b.weight - a.weight).slice(0, 3);
}

/* ─── INFÉRENCE CIRCULATION ──────────────────────────────────────── */

export function inferFromCirculation(form) {
  if (!form) return [];
  const suggestions = [];

  // 1. Critères dynamiques "non" → poids 100
  if (form.criteresRealises) {
    Object.entries(form.criteresRealises).forEach(([k, v]) => {
      if (v === 'non' && CIRCULATION_CRITERES_TO_REMC[k]) {
        pushUnique(suggestions, buildSuggestion(
          CIRCULATION_CRITERES_TO_REMC[k], 100, 'Critère non atteint'
        ));
      }
    });
  }

  // 2. Compétences observées "non" → poids 80
  // Format nouveau : la clé EST déjà un code REMC officiel
  // Format legacy : la clé est un label texte (cf. CIRCULATION_COMP_OBSERVEES_TO_REMC)
  if (form.competencesObservees) {
    Object.entries(form.competencesObservees).forEach(([key, v]) => {
      if (v !== 'non') return;
      // Tentative directe : la clé est-elle un code REMC ?
      if (getLivretItem(key)) {
        pushUnique(suggestions, buildSuggestion(
          key, 80, `« ${getLivretItem(key).intitule} » non maîtrisée`,
        ));
        return;
      }
      // Sinon : fallback ancien mapping label → code
      if (CIRCULATION_COMP_OBSERVEES_TO_REMC[key]) {
        pushUnique(suggestions, buildSuggestion(
          CIRCULATION_COMP_OBSERVEES_TO_REMC[key], 80, `« ${key} » non maîtrisée`,
        ));
      }
    });
  }

  // 3. Appréhensions "important" → poids 70
  if (form.apprehension) {
    Object.entries(form.apprehension).forEach(([k, v]) => {
      if (v === 'important' && CIRCULATION_APPREHENSION_TO_REMC[k]) {
        pushUnique(suggestions, buildSuggestion(
          CIRCULATION_APPREHENSION_TO_REMC[k], 70, 'Appréhension importante'
        ));
      }
    });
  }

  // 4. Type itinéraire "Autoroute / voie rapide" sans habitude correspondante
  if ((form.typeItineraire || []).includes('Autoroute / voie rapide')) {
    pushUnique(suggestions, buildSuggestion('C3-g', 40, 'Itinéraire incluant voie rapide'));
  }

  return suggestions.sort((a, b) => b.weight - a.weight).slice(0, 3);
}
